import React, { useEffect, useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faLocationDot } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ProjectsSection = styled.section`
  padding: 2rem 0;
  overflow: hidden;
  background: ${props => props.theme.colors.background};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  opacity: 0;
  animation: ${fadeUp} 0.8s ease-out forwards;
  animation-delay: 0.3s;
`;

const SectionTitle = styled.h2`
  font-size: clamp(1.75rem, 5vw, 2.5rem);
  color: ${props => props.theme.colors.dark};;
  margin-bottom: clamp(1.5rem, 4vw, 2rem);
  font-weight: 700;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.3;
  
  @media (max-width: 480px) {
    br {
      display: none;
    }
  }
  
  span {
    color: ${props => props.theme.colors.primary};
  }
`;

const Category = styled.h4`
  color: #B5873A;
  font-size: clamp(0.875rem, 2vw, 1rem);
  font-weight: 600;
  margin-bottom: clamp(0.75rem, 2vw, 1rem);
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled.div`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  height: 400px;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.8));
    z-index: 1;
    transition: all 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
    
    &::before {
      background: linear-gradient(to bottom, rgba(153,0,17,0.4), rgba(0,0,0,0.9));
    }
  }
`;

const ProjectImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  height: 100%;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(to top, rgba(0,0,0,0.7), transparent);
  }
`;

const DateBadge = styled.div`
  position: absolute;
  top: clamp(0.75rem, 2vw, 1rem);
  right: clamp(0.75rem, 2vw, 1rem);
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: clamp(0.2rem, 1.5vw, 0.5rem) clamp(0.75rem, 2vw, 1rem);
  border-radius: 15px;
  font-weight: 700;
  z-index: 2;
  text-align: center;
  min-width: 100px;
  
  span {
    display: block;
    font-size: 1.5rem;
    margin-bottom: 0.25rem;
  }
`;

const ProjectContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 2rem;
  color: white;
  z-index: 2;
`;

const ProjectTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: white;
  font-weight: 700;
`;

const ProjectDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  color: rgba(255, 255, 255, 0.9);
  
  div {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 1rem;
    
    svg {
      color: ${props => props.theme.colors.primary};
      font-size: 1.2rem;
    }
  }
`;

const ProjectDescription = styled.p`
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 1.5rem;
  color: rgba(255, 255, 255, 0.85);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ViewDetailsButton = styled.button`
  background: transparent;
  border: 2px solid ${props => props.theme.colors.primary};
  color: white;
  padding: 0.75rem 2rem;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  text-align: center;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
  }
`;

const projects = [
  {
    id: 1,
    slug: "3-years-impact",
    title: "The Big Heart: 3 Years of Impact",
    date: "23th FEB",
    time: "2:00 pm - 7:00 pm",
    location: "Casablanca, Morocco",
    image: "/images/EventPage/Birthday-event.jpg",
    description: "A celebration of three extraordinary years of compassion, dedication, and transformation...",  },
  {
    id: 2,
    slug: "plastics-forum",
    title: "The Big Heart at the International Plastics Forum",
    date: "26th JUN",
    time: "9:00 am - 6:00 pm",
    location: "Casablanca, Morocco",
    image: "/images/EventPage/event-image.jpg",
    description: "Join us at the International Plastics Forum where innovation meets social impact...",
  }
];

function Projects() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.2 }
    );

    const currentElement = sectionRef.current;
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, []);

  return (
    <ProjectsSection ref={sectionRef}>
      <Container>
        <SectionHeader>
          <Category>OUR EVENTS</Category>
          <SectionTitle>Help us build brighter futures for the next generations.</SectionTitle>
        </SectionHeader>
        <ProjectsGrid>
          {projects.map((project) => (
            <ProjectCard key={project.id}>
              <ProjectImage image={project.image} />
              <DateBadge>
                <span>{project.date.split(' ')[0]}</span>
                {project.date.split(' ')[1]}
              </DateBadge>
              <ProjectContent>
                <ProjectTitle>{project.title}</ProjectTitle>
                {/* <ProjectDetails>
                  <div>
                    <FontAwesomeIcon icon={faClock} />
                    {project.time}
                  </div>
                  <div>
                    <FontAwesomeIcon icon={faLocationDot} />
                    {project.location}
                  </div>
                </ProjectDetails> */}
                {/* <ProjectDescription>{project.description}</ProjectDescription> */}
                <Link to={`/events/${project.slug}`}>
                <ViewDetailsButton>View Details</ViewDetailsButton>
                </Link>
              </ProjectContent>
            </ProjectCard>
          ))}
        </ProjectsGrid>
      </Container>
    </ProjectsSection>
  );
}

export default Projects;